import type { MeasureSimulationCandidateDTO } from '@/src/api/types/procurement'
import { BaseText } from '@/src/components/BaseText'
import {
  MeasureCandidateTableRow,
  type MeasureCandidateTableRowData,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/table/MeasureCandidate.tableRow'
import useTableSort from '@/src/hooks/useTableSort.hook'
import { Flex, Table, TableContainer, Tbody, Th, Thead, Tooltip, Tr, chakra } from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const ColGroup = chakra('colgroup')
const Col = chakra('col')

const getMeasureCandidateTableHeaders = (
  t: TFunction
): {
  title: string
  sortable?: boolean
  sortKey?: keyof MeasureSimulationCandidateDTO
  width: string
  minWidth: string
  align?: 'left' | 'right' | 'center'
}[] => {
  return [
    {
      title: t('components.procurement.forecasting.adjustment_table.column_name'),
      sortable: true,
      sortKey: 'name',
      width: '300px',
      minWidth: '180px',
    },
    {
      title: t('components.procurement.forecasting.adjustment_table.column_type'),
      sortable: true,
      sortKey: 'type',
      width: '200px',
      minWidth: '140px',
    },
    {
      title: t('components.procurement.forecasting.adjustment_table.column_location'),
      sortable: true,
      sortKey: 'locationId',
      width: '200px',
      minWidth: '140px',
    },
    {
      title: t('components.procurement.forecasting.adjustment_table.column_delta_measure'),
      sortable: true,
      sortKey: 'consumptionDelta',
      width: '200px',
      minWidth: '200px',
      align: 'right' as const,
    },
    {
      title: '',
      sortable: false,
      width: '60px',
      minWidth: '60px',
    },
  ]
}

const mapMeasureSimulationCandidateToRows = (rows: MeasureSimulationCandidateDTO[], t: TFunction): MeasureCandidateTableRowData[] => {
  return rows.map(r => ({
    ...r,
    type: t(`components.procurement.forecasting.table.measure_simulation_type.${r.type.toLowerCase()}`),
  }))
}

export const MeasureCandidateTable = ({ rows }: { rows: MeasureSimulationCandidateDTO[] }) => {
  const { t } = useTranslation()

  const columnHeaders = useMemo(() => getMeasureCandidateTableHeaders(t), [t])
  const mappedRows = useMemo(() => mapMeasureSimulationCandidateToRows(rows, t), [rows, t])

  const { sortedData, SortArrows } = useTableSort(mappedRows, 'name')

  return (
    <TableContainer overflowX='visible'>
      <Table
        variant='simple'
        size='sm'
        sx={{
          tableLayout: 'fixed',
          minWidth: '940px',
          '& th, & td': { overflow: 'hidden' },
          '& th p': { whiteSpace: 'nowrap' },
          '& th > div': { minWidth: 0 },
        }}
      >
        <ColGroup>
          {columnHeaders.map((header, index) => (
            <Col key={`${header.title}-${index}`} sx={{ width: header.width, minWidth: header.minWidth }} />
          ))}
        </ColGroup>
        <Thead>
          <Tr>
            {columnHeaders.map((header, index) => (
              <Th key={`${String(header.sortKey)}-${index}`} width={header.width} minWidth={header.minWidth} textTransform='none'>
                <Flex gap={2} justifyContent={header.align === 'right' ? 'flex-end' : 'flex-start'} alignItems={'center'} minW={0}>
                  <Tooltip
                    label={header.title}
                    bg='white'
                    color='gray.800'
                    fontWeight='normal'
                    borderRadius='base'
                    hasArrow
                    openDelay={300}
                    isDisabled={!header.title}
                  >
                    <BaseText fontSize={'small'} overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap' minW={0}>
                      {header.title}
                    </BaseText>
                  </Tooltip>
                  {header.sortKey && <SortArrows headKey={String(header.sortKey)} />}
                </Flex>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {sortedData.map(row => (
            <MeasureCandidateTableRow key={row.id} row={row} />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
